import { type CSSProperties,useEffect,useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { ROUTE_PATHS } from '../../routes/routePaths';
import authService from '../../services/authService';
import { useAuthViewDocumentMutation } from '../../services/singleReportService';
import type {
  MtobResourceDocumentsResponse,
  RenderingStyleDocument,
  RenderingStyleDocumentationResponse,
  VendorUserResponse,
} from '../../types/auth';
import { isPdfExtension } from '../../util/downloadRedirect';
import { resolveFileExtensionIconName } from '../../util/fileExtensionIcon';
import { useDocumentDownload } from '../../util/useDocumentDownload';
import { Icon } from '../Icons';

interface TermsAndConditionsResponse {
  data: string;
}

  // Split documents into two columns
  const splitDocumentsIntoColumns = (documentation: Array<RenderingStyleDocument>) => {
    const columnCount = Math.ceil(documentation.length / 2);
    const leftColumn = documentation.slice(0, columnCount);
    const rightColumn = documentation.slice(columnCount);
    return { leftColumn, rightColumn };
  };

const resolveRenderStyleId = (document_: {
  renderStyleId?: number;
  renderStyleID?: number;
  renderingStyleId?: number;
}): number | undefined =>
  document_.renderStyleId ?? document_.renderStyleID ?? document_.renderingStyleId;

const resolveDocumentExtension = (document_: {
  extension?: string;
  fileName?: string;
}): string => {
  const extension = document_.extension?.trim();
  if (extension) return extension;
  const fileName = document_.fileName?.trim();
  if (!fileName) return '';
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.slice(dotIndex + 1) : '';
};

const getDocumentHref = (document_: any): string => {
  if (document_.isLink && document_.urlLink) return document_.urlLink;
  const documentId = document_.docId ?? document_.docID;
  const extension = resolveDocumentExtension(document_);
  // if (!documentId || !extension || !isPdfExtension(extension)) return '#';
  const parameters = new URLSearchParams();
  parameters.set('filename', `${documentId}.${extension}`);
  if (document_.fileDir) parameters.set('path', document_.fileDir);
  const renderStyleId = resolveRenderStyleId(document_);
  if (renderStyleId != null) parameters.set('renderStyleId', String(renderStyleId));
  parameters.set('location', document_.location ?? 'tc');
  if (document_.isMtob != null) parameters.set('isMtob', String(document_.isMtob));
  parameters.set('extension', extension);
  return `${ROUTE_PATHS.CONDITION_OF_USE_DOC_DOWNLOAD}?${parameters.toString()}`;
};

const handleDocumentLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  document_: any,
): void => {
  if (document_.isLink && document_.urlLink) return;
  if (isPdfExtension(resolveDocumentExtension(document_))) return;
  event.preventDefault();
};
const buildMtobDocument = (
  document_: any,
  fallbackRenderStyleId?: number,
) => ({
  ...document_,
  renderingStyleId: resolveRenderStyleId(document_) ?? fallbackRenderStyleId,
  location: document_.propertyName ? 'mastermtob' : 'tc',
  isMtob: 1,
});

const SKELETON_LINE_WIDTHS = ['100%', '92%', '100%', '83%', '100%', '80%', '100%', '67%', '100%', '100%', '100%'];

interface DocumentLinkProps {
  readonly document_: any;
  readonly label: string;
  readonly onRequestDocument: (document_: any) => void;
}

function DocumentLink({ document_, label, onRequestDocument }: DocumentLinkProps) {
  return (
    <Link
      to={getDocumentHref(document_)}
      target="_blank"
      rel="noopener noreferrer"
      className="ao_condOfUseDocLink"
      onClick={(event) => {
        handleDocumentLinkClick(event, document_);
        if (!isPdfExtension(resolveDocumentExtension(document_))) onRequestDocument(document_);
      }}
    >
      <Icon
        name={resolveFileExtensionIconName(resolveDocumentExtension(document_), Number(document_.isLink))}
        size="sm"
      />
      <span>{label}</span>
    </Link>
  );
}

interface DocumentSectionProps {
  readonly title: string;
  readonly leftColumn: React.ReactNode;
  readonly rightColumn: React.ReactNode;
}

function DocumentSection({ title, leftColumn, rightColumn }: DocumentSectionProps) {
  return (
    <div className="ao_condOfUseSection">
      <h3 className="ao_condOfUseSectionTitle">{title}</h3>
      <div className="ao_condOfUseGrid">
        <div className="ao_condOfUseColumn">{leftColumn}</div>
        <div className="ao_condOfUseColumn">{rightColumn}</div>
      </div>
    </div>
  );
}

function ConditionOfUseSkeleton({ showDocuments }: { readonly showDocuments: boolean }) {
  return (
    <div className="ao_condOfUseSkeleton" aria-busy="true" aria-label="Loading conditions of use">
      {SKELETON_LINE_WIDTHS.map((width, index) => (
        <div
          key={`skeleton-line-${index}`}
          className="ao_condOfUseSkeletonLine"
          style={{ width }}
          aria-hidden="true"
        />
      ))}
      {showDocuments && (
        <div className="ao_condOfUseSection ao_condOfUseSkeleton" aria-hidden="true">
          <div className="ao_condOfUseSkeletonLine" style={{ width: '33%' }} />
          <div className="ao_condOfUseGrid">
            {[0, 1].map((column) => (
              <div key={`skeleton-column-${column}`} className="ao_condOfUseColumn">
                {Array.from({ length: 8 }, (_, row) => (
                  <div
                    key={`skeleton-doc-${column}-${row}`}
                    className="ao_condOfUseSkeletonLine"
                    style={{ width: '80%' }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ConditionOfUseModalProps {
  /** Style override for the "Conditions of Use" trigger link — e.g. white text
   * for the error pages' dark footer variant, which this component's own
   * default link styling wouldn't be visible against. */
  readonly linkStyle?: CSSProperties;
}

function ConditionOfUseModal({ linkStyle }: ConditionOfUseModalProps) {
  const [show, setShow] = useState(false);
   const storageMutation = useAuthViewDocumentMutation();
  const [lastRequestedFilename, setLastRequestedFilename] = useState<
    string | null
  >(null);

  const handleClose = () => setShow(false);
  const handleShow = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShow(true);
  };
  const sendDocumentData = (document_:any) => {
    const path = document_.fileDir;
    const documentId = document_.docId ?? document_.docID;
    const extension = resolveDocumentExtension(document_);
    const filename = documentId && extension ? `${documentId}.${extension}` : '';
    const renderStyleId = resolveRenderStyleId(document_);
    const downloadDocumentName = `${document_.docName ?? document_.fileName ?? 'Document'}.${extension}`;
    setLastRequestedFilename(downloadDocumentName);
    storageMutation.mutate({
      filename,
      path,
      renStyleID: renderStyleId,
      type: 'conditionsOfUse',
      compare: 0,
      prodID: renderStyleId,
      location: document_.location ?? 'tc',
      isMtob: document_.isMtob,
    });
  };
    useDocumentDownload({
      storageMutation,
      lastRequestedFilename,
      setLastRequestedFilename,
      skipErrorHandling: true, // Handle error separately to open in new tab
    });

    // Handle error separately - open in new tab for this component
    useEffect(() => {
      if (storageMutation.isError) {
        const status = (storageMutation.error as AxiosError | undefined)?.response?.status;
        let query = 'source=conditionsOfUse';
        if (status === 403) {
          const errorUrl = ROUTE_PATHS.PUBLIC_ACCESS_ERROR;
          window.open(errorUrl, '_blank', 'noopener,noreferrer');
          storageMutation.reset?.();
          return;
        } else if (status === 404) {
          query += '&isDocumentError=true';
        } else {
          query += '&globalError=true';
        }
        const errorUrl = `${ROUTE_PATHS.PUBLIC_STANDARD_ERROR}?${query}`;
        window.open(errorUrl, '_blank', 'noopener,noreferrer');
        storageMutation.reset?.();
      }
    }, [storageMutation.isError, storageMutation]);
  // Query for vendor user status
  const {
    data: vendorUserResponse,
    isLoading: isLoadingVendorUser,
    error: vendorUserError,
  } = useQuery<VendorUserResponse>({
    queryKey: ['vendorUsers'],
    queryFn: async () => {
      return await authService.getVendorUsers() as VendorUserResponse;
    },
    enabled: show,
  });

  const isVendorUser = (vendorUserResponse?.data?.length ?? 0) > 0;

  // Query for terms and conditions content
  const {
    data: termsResponse,
    isLoading: isLoadingTerms,
    error: termsError,
  } = useQuery<TermsAndConditionsResponse>({
    queryKey: ['termsAndConditions'],
    queryFn: async () => {
      return await authService.getTermsCondition() as TermsAndConditionsResponse;
    },
    enabled: show,
  });

  const termsContent = termsResponse?.data ?? '';

  // Query for rendering style documentation (only fetch if not vendor user and first query is done)
  const {
    data: documentationResponse,
    isLoading: isLoadingDocumentation,
    error: documentationError,
  } = useQuery<RenderingStyleDocumentationResponse>({
    queryKey: ['renderingStyleResourceDocs'],
    queryFn: async () => {
      return await authService.getRenderingStyleResourceDocs() as RenderingStyleDocumentationResponse;
    },
    enabled: show && !isLoadingVendorUser && !isVendorUser,
  });

  const {
    data: mtobResponse,
    isLoading: isLoadingMtob,
    error: mtobError,
  } = useQuery<MtobResourceDocumentsResponse>({
    queryKey: ['mtobResourceDocs'],
    queryFn: async () => {
      return await authService.getMtobResourceDocs() as MtobResourceDocumentsResponse;
    },
    enabled: show && !isLoadingVendorUser && !isVendorUser,
  });

  const renderingStyleDocumentation = documentationResponse?.data ?? [];
  const mtobProductSchedules = mtobResponse?.data?.productSchedules ?? [];
  const mtobMasterTermsOfBusiness = mtobResponse?.data?.masterTermsOfBusiness ? {...mtobResponse?.data?.masterTermsOfBusiness, extension: 'pdf'} : null;
  const mtobFallbackRenderStyleId = mtobProductSchedules.find(
    (schedule) => schedule.renderingStyleId != null,
  )?.renderingStyleId;
  const mtobDocuments = [
    ...(mtobResponse?.data?.masterTermsOfBusiness ? [mtobMasterTermsOfBusiness] : []),
    ...mtobProductSchedules,
  ];
  const mtobColumnCount = Math.ceil(mtobDocuments.length / 2);
  const mtobLeftColumn = mtobDocuments.slice(0, mtobColumnCount);
  const mtobRightColumn = mtobDocuments.slice(mtobColumnCount);

  const loading = isLoadingVendorUser || isLoadingTerms || isLoadingDocumentation || isLoadingMtob;
  const error = vendorUserError ?? termsError ?? documentationError ?? mtobError;
  const showStandardDocuments = !isVendorUser && renderingStyleDocumentation.length > 0;
  const hasContent = Boolean(termsContent) || mtobDocuments.length > 0 || showStandardDocuments;

  const { leftColumn, rightColumn } = splitDocumentsIntoColumns(renderingStyleDocumentation);

  const renderMtobLinks = (documents: Array<any>) =>
    documents.map((document_: any) => (
      <DocumentLink
        key={document_.docId ?? document_.propertyName}
        document_={buildMtobDocument(document_, mtobFallbackRenderStyleId)}
        label={document_.fileName ?? document_.propertyVal ?? 'Document'}
        onRequestDocument={sendDocumentData}
      />
    ));

  const renderStandardLinks = (documents: Array<RenderingStyleDocument>) =>
    documents.map((document_) => (
      <DocumentLink
        key={document_.docID ?? `${document_.fileName}-${document_.fileTypeID}`}
        document_={document_}
        label={document_.fileName ?? 'Document'}
        onRequestDocument={sendDocumentData}
      />
    ));

  return (
    <>
      <Link to="#" style={linkStyle} onClick={handleShow}>
        Conditions of Use
      </Link>

      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        keyboard={false}
        centered
        scrollable
        dialogClassName="v3-dialog-lg"
        contentClassName="v3-dialog ao_condOfUseDialog"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h2">Conditions of Use</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <ConditionOfUseSkeleton showDocuments={!vendorUserResponse || !isVendorUser} />
          )}
          {!loading && error && (
            <p className="ao_condOfUseError">
              Unable to load conditions of use. Please try again.
            </p>
          )}
          {!loading && !error && !hasContent && (
            <p className="ao_condOfUseEmpty">
              No conditions of use are available at this time.
            </p>
          )}
          {!loading && !error && hasContent && (
            <>
              {termsContent && (
                <div className="ao_condOfUseTerms" dangerouslySetInnerHTML={{ __html: termsContent }} />
              )}
              {mtobDocuments.length > 0 && (
                <DocumentSection
                  title="Global Master Terms of Business & Product Schedules"
                  leftColumn={renderMtobLinks(mtobLeftColumn)}
                  rightColumn={renderMtobLinks(mtobRightColumn)}
                />
              )}
              {showStandardDocuments && (
                <DocumentSection
                  title="Standard Terms of Business"
                  leftColumn={renderStandardLinks(leftColumn)}
                  rightColumn={renderStandardLinks(rightColumn)}
                />
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="v3-btn v3-btn-outline"
            id="LogOutContinue"
            onClick={handleClose}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ConditionOfUseModal;
